import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Progress } from "../ui/progress";
import {
  User,
  GraduationCap,
  Briefcase,
  Award,
  FileText,
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Download,
  Save,
  Loader2,
  RefreshCw,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { resumeAPI, profileAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import ProfileSyncModal, { fieldLabelFor } from "./ProfileSyncModal";
import useAutosave from "../../hooks/useAutosave";
import AutosaveIndicator from "./AutosaveIndicator";

const TONE_HINT = "Tip: lead each bullet with an action verb and a measurable outcome (e.g. \"Reduced page load time by 38% by …\").";

// Iter-26 Phase 1: Identity fields the resume builder cascades from
// the profile. Editing any of these triggers the 3-way sync modal.
// Keep aligned with PROFILE_CASCADING in backend/server.py.
const CASCADING_KEYS = ["full_name", "location", "linkedin", "website"];

const ResumeBuilder = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resumeExists, setResumeExists] = useState(false);

  // Live PDF preview state (only generated on the Review step).
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const prevBlobUrlRef = useRef(null);
  const previewTimerRef = useRef(null);

  const [resumeData, setResumeData] = useState({
    title: "My Resume",
    personal_info: {
      full_name: user ? `${user.first_name} ${user.last_name}` : "",
      email: user?.email || "",
      phone: "",
      location: "",
      linkedin: "",
      website: "",
    },
    education: [],
    experience: [],
    skills: [],
    certifications: [],
  });

  // Iter-26 Phase 1: master Profile snapshot for the cascade. Loaded
  // once on mount; we compare cascading fields against this when the
  // candidate Saves to decide whether to show the 3-way sync modal.
  const [profile, setProfile] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [pendingChangedFields, setPendingChangedFields] = useState([]);
  // Tracks personal_info values the candidate has touched. The cascade
  // is "smart": on initial load, blank fields auto-fill from the
  // profile; once the user types into a field we treat it as a
  // potential override.
  const touchedFieldsRef = useRef(new Set());

  const steps = [
    { id: 1, title: "Personal Info", icon: User, description: "Basic contact information" },
    { id: 2, title: "Experience", icon: Briefcase, description: "Work history" },
    { id: 3, title: "Education", icon: GraduationCap, description: "Academic background" },
    { id: 4, title: "Skills", icon: Award, description: "Skills & certifications" },
    { id: 5, title: "Review & Download", icon: FileText, description: "Preview and export" },
  ];

  // Load existing resume on mount.
  useEffect(() => {
    loadResume();
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const p = await profileAPI.getProfile();
      setProfile(p);
    } catch (err) {
      // Brand-new users may not have a profile yet — non-fatal.
      console.warn("Could not load profile:", err);
    }
  };

  const loadResume = async () => {
    try {
      setLoading(true);
      const existingResume = await resumeAPI.getResume();
      if (existingResume) {
        setResumeData(existingResume);
        setResumeExists(true);
      }
    } catch (error) {
      console.error("Failed to load resume:", error);
    } finally {
      setLoading(false);
    }
  };

  // Iter-26 Phase 1: smart cascade — when the profile loads, fill any
  // BLANK cascading personal_info fields from the profile, and ALWAYS
  // overwrite identity-LOCKED fields (email, phone) so they reflect
  // the master copy. The user can later override cascading fields by
  // typing into them; locked fields stay read-only.
  useEffect(() => {
    if (!profile) return;
    setResumeData((prev) => {
      const pi = { ...(prev.personal_info || {}) };
      // CASCADING fields: fill if blank.
      CASCADING_KEYS.forEach((k) => {
        if (!pi[k] && profile[k]) pi[k] = profile[k];
      });
      // full_name has a separate cascade path because the resume's
      // doc copy is allowed to be a per-application override.
      if (!pi.full_name && profile.full_name) pi.full_name = profile.full_name;
      // LOCKED fields: always reflect profile so the form mirrors what
      // will actually render on the PDF.
      if (profile.email) pi.email = profile.email;
      if (profile.phone) pi.phone = profile.phone;
      return { ...prev, personal_info: pi };
    });
  }, [profile]);

  // Build the list of cascading fields the user has changed from the
  // profile's master copy. Returns ``[]`` if nothing differs — that's
  // the signal to skip the sync modal and just save.
  const computeChangedCascadingFields = useCallback(() => {
    if (!profile) return [];
    const pi = resumeData.personal_info || {};
    const out = [];
    CASCADING_KEYS.forEach((k) => {
      // Only count it if the user actually touched the field (smart
      // cascade rule). Prevents false positives when the profile
      // value is e.g. empty and the resume field is also empty.
      if (!touchedFieldsRef.current.has(k)) return;
      const docVal = (pi[k] || "").toString().trim();
      const profVal = (profile[k] || "").toString().trim();
      if (docVal !== profVal && docVal) {
        out.push({
          key: k,
          label: fieldLabelFor(k),
          current: docVal,
          previous: profVal,
        });
      }
    });
    return out;
  }, [profile, resumeData.personal_info]);

  const persistResume = useCallback(async (data, { silent = false } = {}) => {
    try {
      if (resumeExists) {
        await resumeAPI.updateResume(data);
      } else {
        const created = await resumeAPI.createResume(data);
        setResumeData(created);
        setResumeExists(true);
      }
      if (!silent) {
        toast.success(resumeExists ? "Resume updated" : "Resume saved");
      }
      return true;
    } catch (error) {
      console.error("Failed to save resume:", error);
      if (!silent) {
        toast.error("Could not save resume — please try again.");
      }
      return false;
    }
  }, [resumeExists]);

  // Iter-39 debounced autosave. We pass `silent: true` so the user
  // doesn't get a toast on every keystroke — visual feedback lives in
  // the AutosaveIndicator next to the Save button.
  const autosaveSaver = useCallback(async (data) => {
    return persistResume(data, { silent: true });
  }, [persistResume]);

  const { status: autosaveStatus, lastSavedAt: autosaveAt, flush: autosaveFlush } = useAutosave(
    resumeData,
    autosaveSaver,
    {
      delay: 1500,
      enabled: !loading,
      shouldSave: (d) => !!(d.personal_info?.full_name || d.experience?.length || d.education?.length || d.skills?.length),
    },
  );

  const saveResume = async () => {
    const changed = computeChangedCascadingFields();
    if (changed.length > 0) {
      setPendingChangedFields(changed);
      setSyncModalOpen(true);
      return;
    }
    setSaving(true);
    await persistResume(resumeData);
    setSaving(false);
  };

  // Modal callback: save resume only — profile untouched.
  const handleSyncDocOnly = async () => {
    setSaving(true);
    setSyncModalOpen(false);
    await persistResume(resumeData);
    setPendingChangedFields([]);
    touchedFieldsRef.current = new Set();
    setSaving(false);
  };

  // Modal callback: save resume + patch the profile so the new
  // cascading values become the master copy and propagate to every
  // other document at render time.
  const handleSyncAll = async () => {
    setSaving(true);
    setSyncModalOpen(false);
    const patch = {};
    pendingChangedFields.forEach((f) => {
      patch[f.key] = f.current;
    });
    try {
      await persistResume(resumeData, { silent: true });
      const updated = await profileAPI.syncFromDocument(patch);
      setProfile(updated);
      toast.success("Resume + profile updated. Changes will apply to all your documents.");
    } catch (err) {
      console.error("Sync to profile failed:", err);
      toast.error("Saved this resume, but couldn't update your profile.");
    } finally {
      setPendingChangedFields([]);
      touchedFieldsRef.current = new Set();
      setSaving(false);
    }
  };

  const handleSyncCancel = () => {
    setSyncModalOpen(false);
    setPendingChangedFields([]);
  };

  // ── Personal info ──
  // Coalesce null/undefined → '' so controlled inputs never receive null
  // (silences React's "value prop on input should not be null" warning
  // when the resume is loaded from DB with optional fields missing).
  const piVal = (key) => resumeData.personal_info?.[key] ?? "";

  const handlePersonalInfoChange = (field, value) => {
    // Mark the field as user-touched so the cascade treats it as a
    // potential override at save time.
    if (CASCADING_KEYS.includes(field)) {
      touchedFieldsRef.current.add(field);
    }
    setResumeData((prev) => ({
      ...prev,
      personal_info: { ...prev.personal_info, [field]: value },
    }));
  };

  // Phone is now a Profile-LOCKED field — kept as a noop so callers
  // can still reference it without crashing.
  const handlePhoneBlur = () => {};

  // ── Experience helpers ──
  const addExperience = () => {
    setResumeData((prev) => ({
      ...prev,
      experience: [
        ...prev.experience,
        {
          company_name: "",
          job_title: "",
          start_date: "",
          end_date: "",
          description: "",
          is_current: false,
        },
      ],
    }));
  };
  const removeExperience = (index) =>
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index),
    }));
  const handleExperienceChange = (index, field, value) =>
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp, i) =>
        i === index ? { ...exp, [field]: value } : exp,
      ),
    }));
  const toggleCurrentExperience = (index, checked) =>
    setResumeData((prev) => ({
      ...prev,
      experience: prev.experience.map((exp, i) =>
        i === index ? { ...exp, is_current: checked, end_date: checked ? "Present" : "" } : exp,
      ),
    }));
  const moveExperience = (index, direction) => {
    setResumeData((prev) => {
      const next = [...prev.experience];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, experience: next };
    });
  };

  // ── Education helpers ──
  const addEducation = () => {
    setResumeData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        {
          school_name: "",
          degree: "",
          field_of_study: "",
          start_date: "",
          end_date: "",
          description: "",
          is_current: false,
        },
      ],
    }));
  };
  const removeEducation = (index) =>
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  const handleEducationChange = (index, field, value) =>
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.map((edu, i) =>
        i === index ? { ...edu, [field]: value } : edu,
      ),
    }));
  const toggleCurrentEducation = (index, checked) =>
    setResumeData((prev) => ({
      ...prev,
      education: prev.education.map((edu, i) =>
        i === index ? { ...edu, is_current: checked, end_date: checked ? "Present" : "" } : edu,
      ),
    }));
  const moveEducation = (index, direction) => {
    setResumeData((prev) => {
      const next = [...prev.education];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, education: next };
    });
  };

  // ── Skills / certifications ──
  const addSkill = () =>
    setResumeData((prev) => ({
      ...prev,
      skills: [...prev.skills, { skill_name: "", proficiency_level: "Intermediate" }],
    }));
  const removeSkill = (index) =>
    setResumeData((prev) => ({ ...prev, skills: prev.skills.filter((_, i) => i !== index) }));
  const handleSkillChange = (index, field, value) =>
    setResumeData((prev) => ({
      ...prev,
      skills: prev.skills.map((skill, i) => (i === index ? { ...skill, [field]: value } : skill)),
    }));

  const addCertification = () =>
    setResumeData((prev) => ({
      ...prev,
      certifications: [
        ...prev.certifications,
        { certification_name: "", issuing_org: "", issue_date: "", expiry_date: "" },
      ],
    }));
  const removeCertification = (index) =>
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  const handleCertificationChange = (index, field, value) =>
    setResumeData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) =>
        i === index ? { ...cert, [field]: value } : cert,
      ),
    }));

  // ── Step navigation with auto-save ──
  const nextStep = async () => {
    if (currentStep < steps.length) {
      // Iter-39: flush autosave first so any in-flight debounced edits
      // land before navigation; then fall back to the explicit save
      // path so the "Next" intent still produces a guaranteed write.
      try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
      await persistResume(resumeData, { silent: true });
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = async () => {
    if (currentStep > 1) {
      try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
      setCurrentStep(currentStep - 1);
    }
  };
  const getStepProgress = () => ((currentStep - 1) / (steps.length - 1)) * 100;

  // ── Live PDF preview (only on Review step) ──
  const generatePreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const blob = await resumeAPI.previewPDF({
        title: resumeData.title || "My Resume",
        personal_info: resumeData.personal_info,
        education: resumeData.education,
        experience: resumeData.experience,
        skills: resumeData.skills,
        certifications: resumeData.certifications,
      });
      const url = window.URL.createObjectURL(blob);
      if (prevBlobUrlRef.current) {
        window.URL.revokeObjectURL(prevBlobUrlRef.current);
      }
      prevBlobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch (err) {
      console.error("Resume preview failed:", err);
      setPreviewError("Could not generate preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [resumeData]);

  useEffect(() => {
    if (currentStep !== steps.length) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(() => {
      generatePreview();
    }, 700);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [currentStep, generatePreview, steps.length]);

  useEffect(
    () => () => {
      if (prevBlobUrlRef.current) {
        window.URL.revokeObjectURL(prevBlobUrlRef.current);
      }
    },
    [],
  );

  // ── Download CTAs ──
  const handleDownloadResume = async () => {
    try {
      // Save first so the server has the latest version.
      await persistResume(resumeData, { silent: true });
      const blob = await resumeAPI.downloadPDF();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (resumeData.personal_info.full_name || "resume")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();
      a.download = `resume-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Resume PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Could not download resume PDF");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="resume-loading">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-600">Loading resume...</p>
        </div>
      </div>
    );
  }

  // ──────────────── Step renderers ────────────────
  const renderPersonalInfo = () => (
    <Card data-testid="resume-step-personal-info">
      <CardHeader>
        <CardTitle className="flex items-center">
          <User className="h-5 w-5 mr-2 text-blue-600" />
          Personal Information
        </CardTitle>
        <CardDescription>This is what recruiters see at the top of your resume.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name *</Label>
            <Input
              id="fullName"
              data-testid="resume-input-full-name"
              value={piVal("full_name")}
              onChange={(e) => handlePersonalInfoChange("full_name", e.target.value)}
              placeholder="John Doe"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-1">
              Email Address *
              <Lock className="h-3 w-3 text-slate-400" />
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="resume-input-email"
              value={piVal("email")}
              readOnly
              disabled
              className="bg-slate-50 cursor-not-allowed"
              placeholder="john@example.com"
            />
            <p className="text-xs text-slate-500">
              Managed in your <strong>Profile</strong>. Update it there
              to change it everywhere.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-1">
              Phone Number
              <Lock className="h-3 w-3 text-slate-400" />
            </Label>
            <Input
              id="phone"
              data-testid="resume-input-phone"
              value={piVal("phone")}
              readOnly
              disabled
              className="bg-slate-50 cursor-not-allowed"
              placeholder="(555) 123-4567"
            />
            <p className="text-xs text-slate-500">
              Managed in your <strong>Profile</strong>.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              data-testid="resume-input-location"
              value={piVal("location")}
              onChange={(e) => handlePersonalInfoChange("location", e.target.value)}
              placeholder="Toronto, ON"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="linkedin">LinkedIn Profile</Label>
            <Input
              id="linkedin"
              data-testid="resume-input-linkedin"
              value={piVal("linkedin")}
              onChange={(e) => handlePersonalInfoChange("linkedin", e.target.value)}
              placeholder="linkedin.com/in/johndoe"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              data-testid="resume-input-website"
              value={piVal("website")}
              onChange={(e) => handlePersonalInfoChange("website", e.target.value)}
              placeholder="johndoe.dev"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderExperience = () => (
    <Card data-testid="resume-step-experience">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
            Work Experience
          </div>
          <Button onClick={addExperience} size="sm" variant="outline" data-testid="resume-add-experience-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Role
          </Button>
        </CardTitle>
        <CardDescription>{TONE_HINT}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {resumeData.experience.length === 0 ? (
          <div className="text-center py-8">
            <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No experience added yet. Click "Add Role" to get started.</p>
          </div>
        ) : (
          resumeData.experience.map((exp, index) => (
            <div key={index} className="border rounded-lg p-4" data-testid={`resume-experience-card-${index}`}>
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium">Experience {index + 1}</h4>
                <div className="flex items-center gap-1">
                  <Button
                    onClick={() => moveExperience(index, -1)}
                    size="sm"
                    variant="ghost"
                    disabled={index === 0}
                    data-testid={`resume-experience-up-${index}`}
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => moveExperience(index, 1)}
                    size="sm"
                    variant="ghost"
                    disabled={index === resumeData.experience.length - 1}
                    data-testid={`resume-experience-down-${index}`}
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => removeExperience(index)}
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700"
                    data-testid={`resume-experience-remove-${index}`}
                    aria-label="Remove"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Title *</Label>
                  <Input
                    value={exp.job_title}
                    onChange={(e) => handleExperienceChange(index, "job_title", e.target.value)}
                    placeholder="Senior Software Engineer"
                    data-testid={`resume-experience-title-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company *</Label>
                  <Input
                    value={exp.company_name}
                    onChange={(e) => handleExperienceChange(index, "company_name", e.target.value)}
                    placeholder="TechCorp"
                    data-testid={`resume-experience-company-${index}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={exp.start_date}
                      onChange={(e) => handleExperienceChange(index, "start_date", e.target.value)}
                      data-testid={`resume-experience-start-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={exp.is_current ? "" : exp.end_date}
                      disabled={!!exp.is_current}
                      onChange={(e) => handleExperienceChange(index, "end_date", e.target.value)}
                      data-testid={`resume-experience-end-${index}`}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 md:mt-7">
                  <Checkbox
                    id={`exp-current-${index}`}
                    checked={!!exp.is_current}
                    onCheckedChange={(c) => toggleCurrentExperience(index, !!c)}
                    data-testid={`resume-experience-current-${index}`}
                  />
                  <Label htmlFor={`exp-current-${index}`} className="cursor-pointer text-sm">
                    I currently work here
                  </Label>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={exp.description}
                    onChange={(e) => handleExperienceChange(index, "description", e.target.value)}
                    placeholder="• Led migration to Kubernetes, cut p99 latency 40%&#10;• Mentored 6 engineers"
                    rows={3}
                    data-testid={`resume-experience-desc-${index}`}
                  />
                  <p className="text-xs text-gray-400 text-right">
                    {(exp.description || "").length} chars
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );

  const renderEducation = () => (
    <Card data-testid="resume-step-education">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <GraduationCap className="h-5 w-5 mr-2 text-blue-600" />
            Education
          </div>
          <Button onClick={addEducation} size="sm" variant="outline" data-testid="resume-add-education-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Education
          </Button>
        </CardTitle>
        <CardDescription>List your most recent or relevant degrees first.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {resumeData.education.length === 0 ? (
          <div className="text-center py-8">
            <GraduationCap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No education added yet. Click "Add Education" to get started.</p>
          </div>
        ) : (
          resumeData.education.map((edu, index) => (
            <div key={index} className="border rounded-lg p-4" data-testid={`resume-education-card-${index}`}>
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium">Education {index + 1}</h4>
                <div className="flex items-center gap-1">
                  <Button
                    onClick={() => moveEducation(index, -1)}
                    size="sm"
                    variant="ghost"
                    disabled={index === 0}
                    data-testid={`resume-education-up-${index}`}
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => moveEducation(index, 1)}
                    size="sm"
                    variant="ghost"
                    disabled={index === resumeData.education.length - 1}
                    data-testid={`resume-education-down-${index}`}
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => removeEducation(index)}
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700"
                    data-testid={`resume-education-remove-${index}`}
                    aria-label="Remove"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>School *</Label>
                  <Input
                    value={edu.school_name}
                    onChange={(e) => handleEducationChange(index, "school_name", e.target.value)}
                    placeholder="University of Waterloo"
                    data-testid={`resume-education-school-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Degree *</Label>
                  <Input
                    value={edu.degree}
                    onChange={(e) => handleEducationChange(index, "degree", e.target.value)}
                    placeholder="B.Sc."
                    data-testid={`resume-education-degree-${index}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Field of Study *</Label>
                  <Input
                    value={edu.field_of_study}
                    onChange={(e) => handleEducationChange(index, "field_of_study", e.target.value)}
                    placeholder="Computer Science"
                    data-testid={`resume-education-field-${index}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={edu.start_date}
                      onChange={(e) => handleEducationChange(index, "start_date", e.target.value)}
                      data-testid={`resume-education-start-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={edu.is_current ? "" : edu.end_date}
                      disabled={!!edu.is_current}
                      onChange={(e) => handleEducationChange(index, "end_date", e.target.value)}
                      data-testid={`resume-education-end-${index}`}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 md:mt-7">
                  <Checkbox
                    id={`edu-current-${index}`}
                    checked={!!edu.is_current}
                    onCheckedChange={(c) => toggleCurrentEducation(index, !!c)}
                    data-testid={`resume-education-current-${index}`}
                  />
                  <Label htmlFor={`edu-current-${index}`} className="cursor-pointer text-sm">
                    I'm currently studying here
                  </Label>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={edu.description}
                    onChange={(e) => handleEducationChange(index, "description", e.target.value)}
                    placeholder="Dean's List, relevant coursework, GPA…"
                    rows={2}
                    data-testid={`resume-education-desc-${index}`}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );

  const renderSkillsAndCertifications = () => (
    <div className="space-y-6" data-testid="resume-step-skills">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Award className="h-5 w-5 mr-2 text-blue-600" />
              Skills
            </div>
            <Button onClick={addSkill} size="sm" variant="outline" data-testid="resume-add-skill-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Skill
            </Button>
          </CardTitle>
          <CardDescription>List the tools, languages, and frameworks you're strongest in.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {resumeData.skills.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No skills added yet.</p>
          ) : (
            resumeData.skills.map((skill, index) => (
              <div key={index} className="flex items-center space-x-4" data-testid={`resume-skill-row-${index}`}>
                <Input
                  className="flex-1"
                  value={skill.skill_name}
                  onChange={(e) => handleSkillChange(index, "skill_name", e.target.value)}
                  placeholder="Python"
                  data-testid={`resume-skill-name-${index}`}
                />
                <select
                  value={skill.proficiency_level}
                  onChange={(e) => handleSkillChange(index, "proficiency_level", e.target.value)}
                  className="w-40 p-2 border rounded-md"
                  data-testid={`resume-skill-level-${index}`}
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                </select>
                <Button
                  onClick={() => removeSkill(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700"
                  data-testid={`resume-skill-remove-${index}`}
                >
                  <Minus className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <Award className="h-5 w-5 mr-2 text-blue-600" />
              Certifications
            </div>
            <Button onClick={addCertification} size="sm" variant="outline" data-testid="resume-add-cert-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Certification
            </Button>
          </CardTitle>
          <CardDescription>Optional — add any active professional certifications.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {resumeData.certifications.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No certifications added yet.</p>
          ) : (
            resumeData.certifications.map((cert, index) => (
              <div key={index} className="border rounded-lg p-4" data-testid={`resume-cert-card-${index}`}>
                <div className="flex justify-between items-start mb-4">
                  <h4 className="font-medium">Certification {index + 1}</h4>
                  <Button
                    onClick={() => removeCertification(index)}
                    size="sm"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700"
                    data-testid={`resume-cert-remove-${index}`}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Certification Name *</Label>
                    <Input
                      value={cert.certification_name}
                      onChange={(e) => handleCertificationChange(index, "certification_name", e.target.value)}
                      placeholder="AWS Certified Solutions Architect"
                      data-testid={`resume-cert-name-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Issuing Organization *</Label>
                    <Input
                      value={cert.issuing_org}
                      onChange={(e) => handleCertificationChange(index, "issuing_org", e.target.value)}
                      placeholder="Amazon Web Services"
                      data-testid={`resume-cert-org-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={cert.issue_date}
                      onChange={(e) => handleCertificationChange(index, "issue_date", e.target.value)}
                      data-testid={`resume-cert-issue-${index}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={cert.expiry_date}
                      onChange={(e) => handleCertificationChange(index, "expiry_date", e.target.value)}
                      data-testid={`resume-cert-expiry-${index}`}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderReview = () => (
    <Card data-testid="resume-step-review">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            Review & Download
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={generatePreview}
              size="sm"
              variant="outline"
              disabled={previewLoading}
              data-testid="resume-refresh-preview-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${previewLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleDownloadResume}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="resume-download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-1" /> Download Resume PDF
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Quick check before you export — your resume preview is rendered exactly as recruiters will see it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-hidden bg-gray-50" style={{ height: "780px" }}>
          {previewError ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <p className="text-red-500 mb-3">{previewError}</p>
              <Button onClick={generatePreview} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-1" /> Try again
              </Button>
            </div>
          ) : previewLoading && !pdfBlobUrl ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Generating preview…</span>
            </div>
          ) : pdfBlobUrl ? (
            <object
              data={pdfBlobUrl}
              type="application/pdf"
              className="w-full h-full"
              data-testid="resume-pdf-preview-iframe"
              aria-label="Resume PDF Preview"
            >
              {/* Fallback for browsers that won't render PDFs inline. */}
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <FileText className="h-12 w-12 text-gray-300 mb-2" />
                <p className="text-gray-600 mb-3">
                  Your browser can't show PDFs inline.
                </p>
                <a
                  href={pdfBlobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
                  data-testid="resume-pdf-preview-fallback-link"
                >
                  <Download className="h-4 w-4" /> Open preview in a new tab
                </a>
              </div>
            </object>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <FileText className="h-12 w-12 text-gray-300 mb-2" />
              <p className="text-gray-500 mb-3">No preview yet.</p>
              <Button onClick={generatePreview} variant="outline" size="sm">
                Generate preview
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderPersonalInfo();
      case 2: return renderExperience();
      case 3: return renderEducation();
      case 4: return renderSkillsAndCertifications();
      case 5: return renderReview();
      default: return renderPersonalInfo();
    }
  };

  return (
    <div className="space-y-8" data-testid="resume-builder">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Resume Builder</h1>
          <p className="text-gray-600 mt-1">Create your branded, ATS-ready resume.</p>
        </div>
        <div className="flex items-center space-x-3">
          <AutosaveIndicator status={autosaveStatus} lastSavedAt={autosaveAt} />
          <Button
            onClick={saveResume}
            disabled={saving}
            variant="outline"
            data-testid="resume-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" /> Save
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          {steps.map((step) => (
            <div
              key={step.id}
              className={`flex items-center space-x-2 ${
                step.id === currentStep
                  ? "text-blue-600"
                  : step.id < currentStep
                  ? "text-green-600"
                  : "text-gray-400"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                  step.id === currentStep
                    ? "border-blue-600 bg-blue-50"
                    : step.id < currentStep
                    ? "border-green-600 bg-green-50"
                    : "border-gray-300"
                }`}
              >
                <step.icon className="h-4 w-4" />
              </div>
              <div className="hidden sm:block">
                <div className="font-medium text-sm">{step.title}</div>
                <div className="text-xs opacity-75">{step.description}</div>
              </div>
            </div>
          ))}
        </div>
        <Progress value={getStepProgress()} className="h-2" />
      </div>

      <div className="min-h-[400px]">{renderCurrentStep()}</div>

      <div className="flex justify-between">
        <Button
          onClick={prevStep}
          disabled={currentStep === 1}
          variant="outline"
          data-testid="resume-prev-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-2" /> Previous
        </Button>
        <div className="space-x-2">
          {currentStep < steps.length ? (
            <Button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="resume-next-btn"
            >
              Next <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleDownloadResume}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="resume-final-download-btn"
            >
              <Download className="h-4 w-4 mr-2" /> Download Resume PDF
            </Button>
          )}
        </div>
      </div>

      <ProfileSyncModal
        open={syncModalOpen}
        onClose={handleSyncCancel}
        onChooseDocumentOnly={handleSyncDocOnly}
        onChooseSyncAll={handleSyncAll}
        documentLabel="Resume"
        changedFields={pendingChangedFields}
        busy={saving}
      />
    </div>
  );
};

export default ResumeBuilder;
